/* eslint-disable spire/export-styles */
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import saveOrder from "@dana/Overrides/Store/Pages/Cart/Handlers/SaveOrder";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { Cart } from "@insite/client-framework/Services/CartService";


type OwnProps =
    | {
        variant: "button";
        extendedStyles?: ButtonPresentationProps;
    }
    | {
        variant: "clickable";
        extendedStyles?: ClickablePresentationProps;
    };

const mapDispatchToProps = {
    saveOrder,
};

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartState = getCurrentCartState(state);
    const cartMode = getCookie('cartMode');
    const cart: Readonly<Cart> | undefined =
        cartMode === 'EmergencyOrderCart'
            ? (state.emergencyCartState.cart as Cart) ?? { properties: {} } as Cart
            : cartMode === 'StockOrderCart'
            ? cartState.value
            : (state.annualReturnsCartState.cart as Cart) ?? { properties: {} } as Cart;
    const { isSavingOrder } = state.pages.cart;
    const { isUpdatingCart } = state.context;
    return {
        isDisabled:
            cartState.isLoading ||
            isSavingOrder ||
            isUpdatingCart ||
            cart?.lineCount === 0 ||
            cart?.cartLines?.length === 0,
        canSaveOrder: cart?.canSaveOrder && cartMode === 'StockOrderCart',
        isSignedIn: cart?.isAuthenticated && !cart?.isGuestOrder,
        savedOrdersPageUrl: getPageLinkByPageType(state, "SavedOrderDetailsPage")?.url,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext &
    ResolveThunks<typeof mapDispatchToProps>;

const CartSaveOrderButton = ({
    isDisabled,
    canSaveOrder,
    saveOrder,
    history,
    toaster,
    savedOrdersPageUrl,
    cartPageUrl,
    signInPageUrl,
    extendedStyles,
    isSignedIn,
    variant,
}: Props) => {
    if (!canSaveOrder) {
        return null;
    }
    const RenderComponent = variant === "button" ? Button : Clickable;
    const handleClick = () => {
        if (!isSignedIn) {
            history.push(`${signInPageUrl}?returnUrl=${cartPageUrl}`);
            return;
        }

        saveOrder({
            onSuccess: (orderId: string) => {
                history.push(`${savedOrdersPageUrl}?cartId=${orderId}`);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
            onError: (errorMessage: string) => {
                toaster.addToast({ body: errorMessage, messageType: "danger" });
            },
        });
    };

    return (
        <RenderComponent
            disabled={isDisabled}
            data-test-selector="cart_saveOrder"
            {...extendedStyles}
            onClick={handleClick}
        >
            {translate("Save Order")}
        </RenderComponent>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(CartSaveOrderButton)));
